"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode, type SVGProps } from "react";
import { customerSupportTelHref, isSiteActive } from "@/shared/config/customerSupport";
import { homePageSeoContent, seoLocality } from "@/shared/config/seo";
import styles from "./CorrectedLandingContent.module.scss";

const ASSET_ROOT = "/assets/corrected/landing";
const GOOGLE_PLAY_URL =
  process.env.NEXT_PUBLIC_GOOGLE_PLAY_URL ||
  process.env.NEXT_PUBLIC_ANDROID_APP_URL ||
  "https://play.google.com/store/search?q=Carevinco&c=apps";
const APP_STORE_URL =
  process.env.NEXT_PUBLIC_APP_STORE_URL ||
  process.env.NEXT_PUBLIC_IOS_APP_URL ||
  "https://apps.apple.com/us/iphone/search?term=Carevinco";

const heroAvatars = Array.from({ length: 5 }, (_, index) => `${ASSET_ROOT}/family-${index + 1}-64.webp`);

const steps = [
  { number: "01", label: "STEP 01", title: "Tell Us What You Need", tone: "stepBlue" },
  { number: "02", label: "STEP 02", title: "Choose Your Nurse", tone: "stepOrange" },
  { number: "03", label: "STEP 03", title: "Care Begins at Home", tone: "stepGreen" },
  { number: "04", label: "STEP 04", title: "Stay Updated", tone: "stepPurple" },
] as const;

type IconProps = SVGProps<SVGSVGElement>;

function HeartIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
    </svg>
  );
}

function PhoneIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M5.3 3.8h3l1.5 4.4-2 1.7a14.8 14.8 0 0 0 6.3 6.3l1.7-2 4.4 1.5v3a1.5 1.5 0 0 1-1.5 1.5C10.5 20.2 3.8 13.5 3.8 5.3a1.5 1.5 0 0 1 1.5-1.5Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function CalendarIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <rect height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" width="18" x="3" y="5" />
      <path d="M7 3v4m10-4v4M3 10h18m-12 5 2 2 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function StarIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" {...props}>
      <path d="m12 2.8 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9L6.4 20l1.1-6.2L3 9.4l6.2-.9L12 2.8Z" fill="currentColor" />
    </svg>
  );
}

function LockIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 32 40" {...props}>
      <rect height="22" rx="5" stroke="currentColor" strokeWidth="2.2" width="26" x="3" y="15" />
      <path d="M9 15V9a7 7 0 0 1 14 0v6M16 24v5" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
    </svg>
  );
}

function ShieldIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 40 44" {...props}>
      <path d="M20 2.5 35 8v11.4c0 9.6-6.1 17.5-15 21.8C11.1 36.9 5 29 5 19.4V8l15-5.5Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2.4" />
      <path d="m13.5 21 4.2 4.2 8.8-9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
    </svg>
  );
}

function SupportIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 44 44" {...props}>
      <path d="M7 23v-3a15 15 0 0 1 30 0v3M7 23v8a4 4 0 0 0 4 4h4V22h-4a4 4 0 0 0-4 4m30-3v8a4 4 0 0 1-4 4h-4V22h4a4 4 0 0 1 4 4M29 35c0 3-2.4 5-5.5 5H21" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.3" />
    </svg>
  );
}

function ChevronIcon({ open, ...props }: IconProps & { open: boolean }) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="m5 9 7 7 7-7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.3" />
    </svg>
  );
}

function GooglePlayBadge() {
  return (
    <svg aria-hidden="true" className={styles.googlePlayBadge} viewBox="0 0 168 50">
      <rect fill="#e6eef2" height="49" rx="6" stroke="#d5e2e6" width="167" x=".5" y=".5" />
      <path d="m14 9 19 16-19 16V9Z" fill="#18b85a" />
      <path d="m14 9 12 12-4 4-8-16Z" fill="#24a5f2" />
      <path d="m14 41 12-12-4-4-8 16Z" fill="#f04d4b" />
      <path d="m26 21 8 4-8 4-4-4 4-4Z" fill="#f8c33c" />
      <text fill="#202124" fontFamily="Arial, sans-serif" fontSize="8" x="47" y="16">GET IT ON</text>
      <text fill="#111" fontFamily="Arial, sans-serif" fontSize="19" fontWeight="600" x="47" y="36">Google Play</text>
    </svg>
  );
}

type RevealSectionProps = {
  ariaLabel?: string;
  children: ReactNode;
  className: string;
  id?: string;
};

export function RevealSection({ ariaLabel, children, className, id }: RevealSectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion || !("IntersectionObserver" in window)) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { threshold: 0.18 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      aria-label={ariaLabel}
      className={`${styles.sectionBase} ${styles.reveal} ${visible ? styles.revealVisible : ""} ${className}`}
      id={id}
      ref={sectionRef}
    >
      {children}
    </section>
  );
}

export type CorrectedHeroProps = {
  balanceCopy?: boolean;
  breakTitle?: boolean;
  description?: ReactNode;
  imageSrc?: string;
  primaryAction?: HeroAction;
  secondaryAction?: HeroAction;
  showSteps?: boolean;
  title?: ReactNode;
};

export type HeroAction = {
  href: string;
  label: string;
};

function HeroActionLink({ action, className, icon }: { action: HeroAction; className: string; icon: ReactNode }) {
  if (action.href.startsWith("/")) {
    return (
      <Link className={className} href={action.href}>
        {icon}
        {action.label}
      </Link>
    );
  }

  return (
    <a className={className} href={action.href}>
      {icon}
      {action.label}
    </a>
  );
}

export function CorrectedHero({
  balanceCopy = false,
  description = homePageSeoContent.pageContent.heroDescription,
  imageSrc = homePageSeoContent.images.hero.url,
  primaryAction,
  secondaryAction,
  showSteps = true,
  title = (
    <>
      {homePageSeoContent.pageContent.heroTitle.lead}
      <span>{homePageSeoContent.pageContent.heroTitle.emphasis}</span>
    </>
  ),
}: CorrectedHeroProps) {
  return (
    <div className={styles.heroGroup}>
      <RevealSection className={styles.heroSection} id="home">
        <div className={`${styles.heroContainer} ${styles.navAligned} container`}>
          <div className={styles.heroCard}>
            <div className={`${styles.heroCopy} ${styles.stagger} ${balanceCopy ? styles.heroCopyBalanced : ""}`}>
              <span className={styles.heroPill} data-reveal-item>
                <HeartIcon />
                Verified nurses, at your door within hours. Serving {seoLocality.displayName}
              </span>
              <h1 data-reveal-item>{title}</h1>
              <p data-reveal-item>{description}</p>
              <div className={styles.buttonRow} data-reveal-item>
                {secondaryAction ? (
                  <HeroActionLink action={secondaryAction} className={`${styles.button} ${styles.buttonOutline}`} icon={<PhoneIcon />} />
                ) : isSiteActive ? (
                  <a className={`${styles.button} ${styles.buttonOutline}`} href={customerSupportTelHref}>
                    <PhoneIcon />
                    Talk to Care Expert
                  </a>
                ) : null}
                {primaryAction ? (
                  <HeroActionLink action={primaryAction} className={`${styles.button} ${styles.buttonPrimary}`} icon={<CalendarIcon />} />
                ) : (
                  <button className={`${styles.button} ${styles.buttonPrimary}`} data-open-inquiry="true" type="button">
                    <CalendarIcon />
                    Book a Care Visit
                  </button>
                )}
              </div>
              <div className={styles.ratingRow} data-reveal-item>
                <div className={styles.avatarStack}>
                  {heroAvatars.map((avatar, index) => (
                    <span className={styles.avatar} key={avatar}>
                      <Image alt={`Carevinco family ${index + 1}`} fill sizes="36px" src={avatar} />
                    </span>
                  ))}
                </div>
                <strong>4.9/5</strong>
                <StarIcon />
                <span>Trusted by 5000 Families</span>
              </div>
            </div>
            <div className={styles.heroImage} data-reveal-item>
              <Image
                alt={homePageSeoContent.images.hero.alt}
                fill
                fetchPriority="high"
                priority
                sizes="(max-width: 639px) calc(100vw - 68px), (max-width: 1023px) calc(100vw - 112px), 44vw"
                src={imageSrc}
              />
            </div>
          </div>
        </div>
      </RevealSection>

      {showSteps ? (
        <RevealSection ariaLabel="Home nursing in four simple steps" className={styles.stepsSection} id="how-it-works">
          <div className={styles.container}>
            <div className={styles.stepsCard}>
              <div className={styles.stepsLabel}>Home Nursing In 4 Simple Steps</div>
              <div className={`${styles.stepsGrid} ${styles.stagger}`}>
                {steps.map((step) => (
                  <article className={styles.stepItem} data-reveal-item key={step.number}>
                    <span className={`${styles.stepNumber} ${styles[step.tone]}`}>{step.number}</span>
                    <div>
                      <small>{step.label}</small>
                      <p>{step.title}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      ) : null}
    </div>
  );
}

const appBenefits = [
  { Icon: LockIcon, title: "Secure", subtitle: "& Private" },
  { Icon: ShieldIcon, title: "100% Trusted", subtitle: "Providers" },
  { Icon: SupportIcon, title: "24/7 Care", subtitle: "Support" },
] as const;

export function CorrectedAppCta() {
  return (
    <RevealSection className={styles.appSection} id="downloads">
      <div className={`${styles.container} ${styles.appLayout} ${styles.stagger} ${styles.navAligned} container`}>
        <div className={styles.appVisual} data-reveal-item>
          <Image
            alt={homePageSeoContent.images.app.alt}
            height={homePageSeoContent.images.app.height}
            sizes="(max-width: 1023px) 88vw, 45vw"
            src={homePageSeoContent.images.app.url}
            width={homePageSeoContent.images.app.width}
          />
        </div>
        <div className={styles.appCopy} data-reveal-item>
          <header>
            <h2>
              Stay Close, No Matter The Distance.
              <br />
              <span>
                Take Care, Anywhere You Are
                <Image alt="" aria-hidden="true" className={styles.appFlourish} height={65} src={`${ASSET_ROOT}/app-flourish.png`} width={58} />
              </span>
            </h2>
            <p>Download the Carevinco app and manage care with confidence, transparency, and complete peace of mind.</p>
            <p>The Carevinco app helps you stay connected with your loved ones, manage care effortlessly, and receive real-time updates with complete peace of mind.</p>
          </header>
          <div className={styles.storeRow}>
            <a aria-label="Get Carevinco on Google Play" href={GOOGLE_PLAY_URL} rel="noopener noreferrer" target="_blank">
              <GooglePlayBadge />
            </a>
            <a aria-label="Download Carevinco on the App Store" href={APP_STORE_URL} rel="noopener noreferrer" target="_blank">
              <Image alt="Download on the App Store" height={50} src={`${ASSET_ROOT}/app-store.png`} width={167} />
            </a>
          </div>
          <div className={styles.appBenefits}>
            {appBenefits.map(({ Icon, subtitle, title }) => (
              <div key={title}>
                <Icon />
                <span><strong>{title}</strong><small>{subtitle}</small></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

export function CorrectedFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <RevealSection className={styles.faqSection} id="faq">
      <div className={`${styles.container} ${styles.faqLayout}`}>
        <header className={styles.sectionHeading}>
          <p>Frequently Asked Questions</p>
          <h2>Carevinco Premium FAQs</h2>
        </header>
        <div className={`${styles.faqList} ${styles.stagger}`}>
          {homePageSeoContent.faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const buttonId = `corrected-faq-button-${index}`;
            const panelId = `corrected-faq-panel-${index}`;

            return (
              <article className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ""}`} data-reveal-item key={faq.question}>
                <button aria-controls={panelId} aria-expanded={isOpen} id={buttonId} onClick={() => setOpenIndex(isOpen ? -1 : index)} type="button">
                  <span>{faq.question}</span>
                  <ChevronIcon className={styles.faqChevron} open={isOpen} />
                </button>
                <p aria-labelledby={buttonId} hidden={!isOpen} id={panelId} role="region">{faq.answer}</p>
              </article>
            );
          })}
        </div>
      </div>
    </RevealSection>
  );
}
